import { Injectable, inject } from '@angular/core';
import {
  Observable,
  catchError,
  forkJoin,
  map,
  of,
  shareReplay,
  switchMap,
  throwError,
} from 'rxjs';
import { TenantSwitchCoordinatorService } from '../tenant-switch/tenant-switch-coordinator.service';
import { TenantApiService } from '../../shared/services/api/tenant-api.service';
import { UserApiService } from '../../shared/services/api/user-api.service';
import { TenantContextService } from './tenant-context.service';
import { TenantBootstrapResult, TenantOption } from './tenant-lifecycle.models';

@Injectable({ providedIn: 'root' })
export class TenantLifecycleService {
  private userApi = inject(UserApiService);
  private tenantApi = inject(TenantApiService);
  private tenantContext = inject(TenantContextService);
  private switchCoordinator = inject(TenantSwitchCoordinatorService);

  /**
   * URLs already kicked off for preload. Avoids re-issuing requests on
   * repeated bootstraps (e.g. test re-creation) without hitting the
   * network twice for the same logo.
   */
  private readonly preloadedLogoUrls = new Set<string>();

  /**
   * Memoized bootstrap stream. Multiple subscribers (AppLayout's ngOnInit
   * and route guards that gate on `tenantContext.isSuperUser()`) share a
   * single in-flight request and the resolved result via `shareReplay(1)`,
   * which is critical to avoid:
   *
   * - duplicate `/me` + `/tenants` HTTP fetches when a route guard runs in
   *   parallel with AppLayout's ngOnInit;
   * - guards racing past tenant resolution and reading the default
   *   ``isSuperUser = false`` for a super-user on a direct deep-link.
   *
   * The replay buffer is intentionally of size 1: the bootstrap result is
   * stable for the session and we want late subscribers to receive it
   * without re-issuing HTTP work.
   */
  private bootstrap$: Observable<TenantBootstrapResult> | null = null;

  bootstrapTenantContext(): Observable<TenantBootstrapResult> {
    if (this.bootstrap$) {
      return this.bootstrap$;
    }
    this.bootstrap$ = forkJoin({
      user: this.userApi.getUserData(),
      tenants: this.tenantApi.getTenants().pipe(catchError(() => of(null))),
    }).pipe(
      switchMap(({ user, tenants }) => {
        const userType = user.data.user_type;
        this.tenantContext.setUserType(userType);
        const isSuperUser = userType === 'super_user';
        const canSwitchTenants = isSuperUser || userType === 'multi_tenant_user';
        const selectedTenantKey = user.data.selected_tenant?.tenant_key ?? null;
        const tenantOptions: TenantOption[] = (tenants?.data ?? []).map((tenant) => ({
          label: tenant.tenant_name,
          value: tenant.tenant_key,
          logo_url: tenant.logo_url ?? null,
        }));
        tenantOptions.sort((a, b) => a.label.localeCompare(b.label));

        if (!tenantOptions.length) {
          this.tenantContext.clearTenantData();
          return throwError(() => new Error('No tenants available for current user.'));
        }

        this.tenantContext.setTenantList(tenantOptions);
        // Warm the browser image cache for every tenant the user can
        // switch to. The active tenant's logo is rendered via `<img>`
        // momentarily after bootstrap; siblings get cached in the
        // background so the *next* tenant switch resolves from cache
        // instead of paying a fresh S3 round-trip. Fire-and-forget — the
        // browser handles dedupe, retries, and lifetime via its HTTP
        // cache; we just keep a local set to avoid issuing the same GET
        // twice within a single page load.
        this.preloadTenantLogos(tenantOptions);
        const selectedTenant = this.resolveInitialTenantSelection({
          tenantOptions,
          selectedTenantKey,
        });

        return this.switchCoordinator.switchTenantAndBootstrap(selectedTenant).pipe(
          map((switchResult) => ({
            isSuperUser,
            canSwitchTenants,
            tenantOptions,
            selectedTenant,
            switchResult,
          }))
        );
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.bootstrap$;
  }

  /**
   * Kick off a background fetch for each tenant's logo so subsequent
   * switches resolve from the browser HTTP cache rather than hitting S3.
   * No-op for tenants with `logo_url: null` (no request would be made
   * for the live `<img>` either), and no-op outside a browser runtime
   * (e.g. SSR/Node test envs that have no global `Image`). Errors are
   * swallowed by the browser — the visible `<img>` will surface them
   * later through its `onerror` handler if the URL really is bad.
   */
  private preloadTenantLogos(tenants: TenantOption[]): void {
    if (typeof Image === 'undefined') return;
    for (const tenant of tenants) {
      const url = tenant.logo_url;
      if (!url || this.preloadedLogoUrls.has(url)) continue;
      this.preloadedLogoUrls.add(url);
      const img = new Image();
      // Match the visible `<img decoding="async">` so the browser uses
      // the same decode path / cache key for both the preload and the
      // later render.
      img.decoding = 'async';
      img.src = url;
    }
  }

  private resolveInitialTenantSelection(input: {
    tenantOptions: TenantOption[];
    selectedTenantKey: string | null;
  }): TenantOption {
    const { tenantOptions, selectedTenantKey } = input;
    const currentTenant = this.tenantContext.getCurrentTenant();
    // Always prefer the freshly-fetched option from the API over the
    // `localStorage` snapshot so newly-added fields (e.g. `logo_url`) aren't
    // dropped on rehydrate. The stored `currentTenant` is kept only as a
    // last-resort fallback for the unlikely case it isn't in the fresh list.
    const freshMatchForCurrent = currentTenant.value
      ? tenantOptions.find((tenant) => tenant.value === currentTenant.value)
      : undefined;

    if (selectedTenantKey) {
      const selectedFromProfile = tenantOptions.find(
        (tenant) => tenant.value === selectedTenantKey
      );
      if (selectedFromProfile) {
        return selectedFromProfile;
      }
      if (freshMatchForCurrent) {
        return freshMatchForCurrent;
      }
      return tenantOptions[0];
    }
    if (freshMatchForCurrent) {
      return freshMatchForCurrent;
    }
    return tenantOptions[0];
  }
}
